import React from "react";
import PrintableSheet from "./PrintableSheet";
import { CheckSquare } from "lucide-react";
import { colors } from "../../config/site";

const SECTIONS = [
  {
    title: "Before You Move In",
    items: [
      "Confirm move-in date and time with landlord",
      "Get keys / fobs / mailbox key — note the count",
      "Take time-stamped photos and a short video of every room (empty)",
      "Test every light switch, outlet, faucet, toilet, and appliance",
      "Check smoke detectors and carbon monoxide detectors (replace batteries)",
      "Verify locks on every door and window work properly",
      "Confirm the unit has been professionally cleaned",
      "Review your signed lease and store a copy in a safe place",
    ],
  },
  {
    title: "Document Existing Conditions",
    items: [
      "Walls and ceilings — note any holes, stains, or marks",
      "Floors and carpets — photograph any tears, stains, or damage",
      "Windows and screens — check for cracks or rips",
      "Kitchen cabinets and counters — note chips, scratches, missing handles",
      "Bathroom — check for mold, caulking issues, leaks under sinks",
      "Appliances — record model numbers and any pre-existing damage",
      "Submit a written move-in inspection report to your landlord within 48 hours",
      "Keep your own dated copy with photos attached",
    ],
  },
  {
    title: "Set Up Utilities",
    items: [
      "Electric (PECO) — call 1-800-494-4000 or peco.com",
      "Gas (PGW) — call 215-235-1000 or pgworks.com",
      "Water — Philadelphia Water Dept., call 215-685-6300",
      "Internet / cable — schedule installation 1–2 weeks in advance",
      "Trash and recycling — confirm pickup day and bin location",
      "Update your address with USPS (usps.com/move)",
      "Update address with bank, employer, insurance, and government IDs",
    ],
  },
  {
    title: "Safety & Health Check",
    items: [
      "Test smoke alarms — press and hold the test button",
      "Test carbon monoxide detectors on each level",
      "Locate the breaker box and label switches",
      "Locate the main water shutoff valve",
      "Locate the gas shutoff valve (if applicable)",
      "Check for visible signs of pests (droppings, bites, holes)",
      "Make sure all exterior doors have working deadbolts",
      "Buy a small fire extinguisher for the kitchen",
    ],
  },
  {
    title: "First-Week Essentials",
    items: [
      "Toilet paper, paper towels, and basic cleaning supplies",
      "Shower curtain, bath mat, and basic towels",
      "Trash bags and a few trash cans",
      "Light bulbs (check what types each fixture uses)",
      "Basic tool kit (screwdriver, hammer, tape measure)",
      "First-aid kit and flashlight",
      "Coffee/tea, snacks, and one easy meal for moving day",
    ],
  },
  {
    title: "Important Contacts to Save",
    items: [
      "Landlord / property manager — name, phone, email",
      "Emergency maintenance line (for nights and weekends)",
      "Building management or HOA office (if applicable)",
      "Nearest fire and police non-emergency lines",
      "Your renter's insurance company and policy number",
      "Dorsey Taylor Homes — 267-210-0255 (for housing stability support)",
    ],
  },
];

const MoveInChecklist = () => {
  return (
    <PrintableSheet
      eyebrow="RENTERS · MOVE-IN CHECKLIST"
      title="Your Move-In Checklist"
      subtitle="A step-by-step list to make sure your new home is documented, set up, and ready for you and your family from day one."
      backHref="/resources/renters"
      backLabel="Back to Renter Resources"
      testId="move-in-checklist-page"
    >
      <div className="space-y-8 print:space-y-6">
        {SECTIONS.map((section, idx) => (
          <section key={section.title} data-testid={`movein-section-${idx}`}>
            <div className="flex items-center gap-2 mb-4">
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                style={{ backgroundColor: colors.primary }}
              >
                {idx + 1}
              </div>
              <h2
                className="text-xl lg:text-2xl font-bold"
                style={{ color: colors.charcoal }}
              >
                {section.title}
              </h2>
            </div>
            <ul className="space-y-2 pl-1">
              {section.items.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 py-1.5 px-3 rounded-lg"
                  style={{ backgroundColor: idx % 2 === 0 ? colors.cream : colors.ivory }}
                >
                  <CheckSquare
                    className="w-5 h-5 mt-0.5 flex-shrink-0"
                    style={{ color: colors.sage }}
                    strokeWidth={2}
                  />
                  <span className="text-sm lg:text-base" style={{ color: colors.charcoal }}>
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}

        {/* Notes section */}
        <section className="pt-4 border-t-2" style={{ borderColor: colors.gold + "55" }}>
          <h2 className="text-xl font-bold mb-3" style={{ color: colors.charcoal }}>
            Personal Notes
          </h2>
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-8 border-b"
                style={{ borderColor: colors.textMuted + "55" }}
              />
            ))}
          </div>
        </section>
      </div>
    </PrintableSheet>
  );
};

export default MoveInChecklist;
